// ============================================================
// SEO registry — every route the site serves, in one list.
//
// <Seo path="…" /> reads its title and description from here, the sitemap
// test checks public/sitemap.xml against `index: true` entries, and the
// react-snap test checks every entry is prerendered. Adding a page means
// adding one entry here (and the route in Routes.js).
//
// `title` is the part before " | Raya ERP" (added by <Seo>) unless
// `fullTitle` is set. Keep the final title ≤ 60 characters and the
// description ≤ 155, unique per page — seo.test.js enforces both.
// ============================================================
import { RATES } from '../website/pricing/pricingData';

export const PAGES = [
  {
    path: '/',
    fullTitle: 'Raya ERP — AI-powered ERP & CRM for Indian businesses',
    description: 'Inventory, sales, purchase, manufacturing, finance and HR in one AI-powered ERP & CRM, with GST, e-Invoice and e-Way Bill built in.',
    index: true, priority: '1.0', changefreq: 'weekly',
  },
  {
    path: '/pricing',
    title: 'Pricing — per user, per month',
    description: `Raya CRM from ₹${RATES.crmOnly} and ERP Standard from ₹${RATES.erpOnly} per user a month. Add-ons, Raya AI seats, yearly billing and an instant estimate.`,
    index: true, priority: '0.9', changefreq: 'monthly',
  },
  {
    path: '/ai',
    title: 'Raya AI — an assistant that does the work',
    description: 'Raya AI drafts bills, expenses and invoices, reconciles your bank, finds stock and forecasts demand — inside your ERP, through your approvals.',
    index: true, priority: '0.9', changefreq: 'monthly', product: 'Raya AI',
  },
  {
    path: '/point-of-sale',
    title: 'Point of Sale (POS) for Indian retail',
    description: 'Scan-first or touch billing, UPI and split payments, GST invoices, thermal and WhatsApp receipts and Z-reports — posted to Raya ERP instantly.',
    index: true, priority: '0.9', changefreq: 'monthly', product: 'Point of Sale', image: '/og/point-of-sale.png',
  },
  {
    path: '/online-store',
    title: 'Online Store on your live ERP stock',
    description: 'A branded online store on live ERP stock: GST-inclusive prices, Razorpay on your own keys, WhatsApp updates and orders that become sales orders.',
    index: true, priority: '0.8', changefreq: 'monthly', product: 'Online Store', image: '/og/online-store.png',
  },
  {
    path: '/project-management',
    title: 'Project Management with timesheets & P&L',
    description: 'Kanban projects, tasks linked to sales orders and vouchers, timesheets with rates, helpdesk queues with SLAs and a project P&L from your books.',
    index: true, priority: '0.8', changefreq: 'monthly', product: 'Project Management', image: '/og/project-management.png',
  },
  {
    path: '/fleet-management',
    title: 'Fleet Management for transporters',
    description: 'Trips with dispatch checks, rate cards, freight bills to GST invoices, hire bills with 194C TDS, fuel-card and FASTag statements, workshop and papers.',
    index: true, priority: '0.8', changefreq: 'monthly', product: 'Fleet Management', image: '/og/fleet-management.png',
  },
  {
    path: '/contact-us',
    title: 'Contact us — talk to the Raya team',
    description: 'Questions about Raya ERP, a demo or a quote? Write to the team in Ahmedabad, or call us during Indian business hours.',
    index: true, priority: '0.7', changefreq: 'monthly',
  },
  {
    path: '/register',
    title: 'Start your free trial',
    description: 'Create your Raya ERP account in minutes: pick your industry, switch on the modules you need and invite your team. 14-day free trial.',
    index: true, priority: '0.6', changefreq: 'monthly',
  },
  {
    path: '/privacy-policy',
    title: 'Privacy notice, terms & Raya AI terms',
    description: 'How Raya handles personal data under the DPDP Act 2023, your rights and how to use them, our cookies, the Terms of Use and the Raya AI terms.',
    index: true, priority: '0.4', changefreq: 'yearly',
  },
  {
    path: '/privacy-request',
    title: 'Make a data protection request',
    description: 'Ask Raya to access, correct or erase your personal data, withdraw consent, nominate someone or raise a grievance under the DPDP Act 2023.',
    index: true, priority: '0.3', changefreq: 'yearly',
  },
  // Account screens: useful to people, not to search results.
  { path: '/login', title: 'Log in', description: 'Sign in to your Raya ERP account.', index: false },
  { path: '/register-success', title: 'Account created', description: 'Your Raya ERP account is ready.', index: false },
  { path: '/forgot-password', title: 'Forgot your password', description: 'Get a link to reset your Raya ERP password.', index: false },
  { path: '/reset-password', title: 'Choose a new password', description: 'Set a new password for your Raya ERP account.', index: false },
  { path: '*', title: 'Page not found', description: 'This page does not exist on the Raya ERP website.', index: false },
];

export const PAGE_BY_PATH = PAGES.reduce((acc, p) => ({ ...acc, [p.path]: p }), {});

export const fullTitleOf = (page) => page.fullTitle || `${page.title} | Raya ERP`;
